import React, { useEffect, useState } from "react";
import "../CSS/dashboard.css";
import Sidebar from "./Sidebar";

// const API_BASE = "http://localhost:8000"; // FastAPI base URL
import { BASE_API_URL } from "../constants";


const Dashboard = () => {
  const [contributions, setContributions] = useState([]);
  const [summary, setSummary] = useState({
    totalContributions: 0,
    totalAmount: 0,
    uniqueProjects: 0,
    lastContributionDate: null,
    projects: [],
  });
  const [wallet, setWallet] = useState(0);
  const [referralCode, setReferralCode] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCapModal, setShowCapModal] = useState(false);
  const [profitSummary, setProfitSummary] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [capModalTab, setCapModalTab] = useState("milestones");

  useEffect(() => {
    const email = sessionStorage.getItem("email") || "";
    const userId = sessionStorage.getItem("userId") || "";

    if (!email || !userId) {
      console.warn("User email or ID not found in session storage.");
      setLoading(false);
      return;
    }

    const encodedEmail = encodeURIComponent(email);
    const encodedUserId = encodeURIComponent(userId);

    // 1 Fetch user profile (including referral code)
    const fetchProfile = fetch(`${BASE_API_URL}/api/user-profile?email=${encodedEmail}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.user) {
          setReferralCode(data.user.referralCode || "");
        }
      })
      .catch((err) => console.error("Error fetching profile:", err));

    // 2 Fetch contributions summary
    const fetchSummary = fetch(`${BASE_API_URL}/api/contributions/summary?userId=${encodedUserId}`)
      .then((res) => res.json());

    // 3 Fetch recent contributions
    const fetchRecent = fetch(`${BASE_API_URL}/api/contributions/recent?userId=${encodedUserId}&limit=10`)
      .then((res) => res.json());

    // 4 Fetch wallet balance
    const fetchWallet = fetch(`${BASE_API_URL}/api/contributor/wallet?userId=${encodedUserId}`)
      .then((res) => res.json())
      .then((data) => setWallet(data?.WalletBalance || 0));

    const fetchProfitSummary = fetch(
      `${BASE_API_URL}/api/contributions/profit-summary?userId=${encodedUserId}`
    )
      .then((res) => res.json())
      .then((data) => setProfitSummary(data))
      .catch(() => setProfitSummary(null));

    // 6 Fetch referral stats
    const fetchReferralStats = fetch(`${BASE_API_URL}/api/referral/dashboard/${encodedUserId}`)
      .then((res) => res.json())
      .then((data) => setReferralStats(data))
      .catch(() => setReferralStats(null));

    // Run all in parallel
    Promise.all([fetchProfile, fetchSummary, fetchRecent, fetchWallet, fetchProfitSummary, fetchReferralStats])
      .then(([_, summaryData, recentData]) => {
        setSummary(summaryData || {});
        setContributions(Array.isArray(recentData) ? recentData : []);
      })
      .catch((err) => {
        console.error("Error fetching dashboard data", err);
        setContributions([]);
        setSummary({ totalContributions: 0, totalAmount: 0, uniqueProjects: 0, lastContributionDate: null, projects: [] });
        setWallet(0);
        setReferralCode("");
      })
      .finally(() => setLoading(false));
  }, []);

  const formatCurrency = (amt) => {
    if (amt === null || amt === undefined) return "0";
    try {
      return "" + Number(amt).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    } catch {
      return `${amt}`;
    }
  };

  const formatStatusClass = (status) => {
    if (!status) return "pending";
    return status.toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleRedeemClick = () => {
    window.location.href = "/Discover";
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem("userId");
    localStorage.removeItem("email");
    localStorage.removeItem("userName");
    window.location.href = "/login";
  };

  const MILESTONES = [
    { milestone: "Starter", amount: 25000, contributors: 5, reward: "Premium Bluetooth Earbuds or Bluetooth Speaker", title: "Rising Star Partner" },
    { milestone: "Bronze", amount: 100000, contributors: 20, reward: "Smartwatch", title: "Bronze Growth Partner" },
    { milestone: "Silver", amount: 250000, contributors: 50, reward: "Professional Smartphone Gimbal", title: "Silver Growth Partner" },
    { milestone: "Gold", amount: 500000, contributors: 100, reward: "GoPro-Class Action Camera", title: "Gold Growth Partner" },
    { milestone: "Platinum", amount: 1000000, contributors: 200, reward: "Creator Kit (Camera + Tripod + Ring Light)", title: "Platinum Growth Partner" },
    { milestone: "Diamond", amount: 1500000, contributors: 400, reward: "Laptop", title: "Diamond Growth Partner" },
    { milestone: "Elite", amount: 3500000, contributors: 1100, reward: "Professional Camera Package (Sony FX3 Class)", title: "Elite Fundraising Partner" },
    { milestone: "Master", amount: 5000000, contributors: 1700, reward: "Motorcycle Reward Program", title: "Master Fundraising Partner" },
    { milestone: "Legend", amount: 10000000, contributors: 2200, reward: "Car Reward Program - Up to Rs.10 Lakh Value", title: "Cinevestor Legend Partner" },
  ];

  return (
    <div>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="ds-main-content">
        <div className="uv-dashboard-header">
          <h1 className="uv-header-title">
            <i className="fas fa-chart-line"></i> Contributor Dashboard
          </h1>
          <div className="uv-header-actions">
            <div className="uv-login-status">
              <i className="fas fa-circle"></i>{" "}
              Last Contribution:{" "}
              {summary.lastContributionDate
                ? new Date(summary.lastContributionDate).toLocaleString()
                : ""}
            </div>
            <button
              type="button"
              className="uv-btn uv-logout-btn"
              onClick={handleLogout}
            >
              <i className="fas fa-sign-out-alt"></i> Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="uv-stats-grid">
          {/* Wallet Card */}
          <div className="uv-stat-card">
            <div className="uv-stat-card-header">
              <h3 className="uv-stat-card-title">Wallet Balance</h3>
              <div className="uv-stat-card-icon">
                <i className="fas fa-wallet"></i>
              </div>
            </div>
            <div className="uv-stat-card-value">
              {loading ? "Loading..." : formatCurrency(wallet)}
            </div>
            <div className="uv-stat-card-description">Current available balance</div>
            <button
              className="uv-btn uv-btn-primary"
              onClick={handleRedeemClick}
              style={{ marginTop: "10px", backgroundColor: "#e53170", cursor: "pointer" }}
            >
              Redeem Wallet
            </button>
          </div>

          {/* Referral Code Card */}
          <div className="uv-stat-card">
            <div className="uv-stat-card-header">
              <h3 className="uv-stat-card-title">Your Referral Code</h3>
              <div className="uv-stat-card-icon">
                <i className="fas fa-gift"></i>
              </div>
            </div>
            <div className="uv-stat-card-value" style={{ display: "flex", alignItems: "center" }}>
              {loading ? "Loading..." : referralCode || "N/A"}
              {referralCode && !loading && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(referralCode);
                    alert("Referral code copied!");
                  }}
                  style={{
                    marginLeft: "10px",
                    padding: "3px 8px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    backgroundColor: "transparent",
                    // color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                  }}
                >
                  <i className="fas fa-copy"></i>
                </button>
              )}
            </div>
            <div className="uv-stat-card-description">Share this code to refer others</div>
          </div>

          {/* Total Amount */}
          <div className="uv-stat-card">
            <div className="uv-stat-card-header">
              <h3 className="uv-stat-card-title">Total Contributed Amount</h3>
              <div className="uv-stat-card-icon">
                <i className="fas fa-hand-holding-usd"></i>
              </div>
            </div>
            <div className="uv-stat-card-value">
              {loading ? "Loading..." : formatCurrency(summary.totalAmount || 0)}
            </div>
            <div className="uv-stat-card-description">Total successful contribution amount</div>
          </div>

          {/* Total Projects */}
          <div className="uv-stat-card">
            <div className="uv-stat-card-header">
              <h3 className="uv-stat-card-title">Total Contributed Projects</h3>
              <div className="uv-stat-card-icon">
                <i className="fas fa-list"></i>
              </div>
            </div>
            <div className="uv-stat-card-value">{loading ? "..." : summary.totalContributions || 0}</div>
            <div className="uv-stat-card-description">Number of successful projects</div>
          </div>

          {/* CAP Reward Milestone Card */}
          {!loading && summary.capRewards && (
            <div className="uv-stat-card uv-cap-card" onClick={() => setShowCapModal(true)} style={{ cursor: "pointer" }}>
              <div className="uv-stat-card-header">
                <h3 className="uv-stat-card-title">CAP Reward Milestone</h3>
                <div className="uv-stat-card-icon">
                  <i className="fas fa-trophy"></i>
                </div>
              </div>

              {summary.capRewards.currentMilestone ? (
                <>
                  <div className="uv-cap-tier">{summary.capRewards.currentMilestone}</div>
                  <div className="uv-cap-qualifier">
                    {summary.capRewards.qualifiedBy === "amount" && "Qualified via Contribution Amount"}
                    {summary.capRewards.qualifiedBy === "referral_count" && "Qualified via Referral Contributors"}
                    {summary.capRewards.qualifiedBy === "both" && "Qualified via Both Conditions"}
                  </div>
                  <div className="uv-cap-reward">
                    Reward: <strong>{summary.capRewards.currentReward}</strong>
                  </div>
                  {summary.capRewards.nextMilestone && (
                    <div className="uv-cap-both-conditions">
                      <div className="uv-cap-condition-row">
                        <span>Amount: &#8377;{formatCurrency(summary.totalAmount)}</span>
                        <span className="uv-cap-muted">/ &#8377;{Number(summary.capRewards.nextMilestoneAmount).toLocaleString("en-IN")}</span>
                      </div>
                      <div className="uv-cap-condition-row">
                        <span>Referrals: {summary.capRewards.referralContribCount}</span>
                        <span className="uv-cap-muted">/ {summary.capRewards.nextMilestoneContributors} contributors</span>
                      </div>
                      <div className="uv-cap-progress-bar">
                        <div className="uv-cap-progress-fill" style={{ width: `${summary.capRewards.progressPercent}%` }}></div>
                      </div>
                      <div className="uv-stat-card-description">Next: <strong>{summary.capRewards.nextMilestone}</strong></div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="uv-cap-tier uv-cap-locked">Not Yet Unlocked</div>
                  <div className="uv-cap-both-conditions">
                    <div className="uv-cap-condition-row">
                      <span>Need &#8377;25,000 contribution amount</span>
                      <span className="uv-cap-muted">OR</span>
                    </div>
                    <div className="uv-cap-condition-row">
                      <span>Need 5 referral contributors</span>
                      <span className="uv-cap-muted">(have: {summary.capRewards.referralContribCount})</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Special Recognition Title Card */}
          {!loading && summary.capRewards && (
            <div className="uv-stat-card uv-cap-card uv-cap-title-card" onClick={() => setShowCapModal(true)} style={{ cursor: "pointer" }}>
              <div className="uv-stat-card-header">
                <h3 className="uv-stat-card-title">Special Recognition</h3>
                <div className="uv-stat-card-icon" style={{ color: "#e53170" }}>
                  <i className="fas fa-medal"></i>
                </div>
              </div>

              {summary.capRewards.currentTitle ? (
                <>
                  <div className="uv-cap-tier">{summary.capRewards.currentTitle}</div>
                  <div className="uv-cap-both-conditions">
                    <div className="uv-cap-condition-row">
                      <span>Total Contributed: &#8377;{formatCurrency(summary.totalAmount)}</span>
                    </div>
                    <div className="uv-cap-condition-row">
                      <span>Referral Contributors: {summary.capRewards.referralContribCount}</span>
                    </div>
                  </div>
                  {summary.capRewards.nextMilestone && (
                    <div className="uv-cap-next-hint">
                      Next title unlocks at <strong>{summary.capRewards.nextMilestone}</strong> milestone
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="uv-cap-tier uv-cap-locked">No Title Yet</div>
                  <div className="uv-stat-card-description">
                    Reach Starter milestone to earn: <strong>Rising Star Partner</strong>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Total Profit Accrued Card */}
          {!loading && profitSummary && (
            <div className="uv-stat-card uv-profit-card">
              <div className="uv-stat-card-header">
                <h3 className="uv-stat-card-title">Total Returns Accrued</h3>
                <div className="uv-stat-card-icon" style={{ color: "#f5a623" }}>
                  <i className="fas fa-coins"></i>
                </div>
              </div>
              <div className="uv-stat-card-value" style={{ color: "#f5a623" }}>
                ₹{formatCurrency(profitSummary.totalProfitAccrued || 0)}
              </div>
              <div className="uv-stat-card-description">
                Accrued daily over 18-month period
              </div>
            </div>
          )}
        </div>

        {/* My Referral Performance */}
        {!loading && referralStats && referralStats.totals && (
          <div className="uv-recent-investments" style={{ marginTop: "30px" }}>
            <div className="uv-section-header">
              <h2 className="uv-section-title">My Referral Performance</h2>
            </div>
            <div className="uv-stats-grid" style={{ marginBottom: "20px" }}>
              <div className="uv-stat-card">
                <div className="uv-stat-card-header">
                  <h3 className="uv-stat-card-title">Total Shares</h3>
                  <div className="uv-stat-card-icon" style={{ color: "#3b82f6" }}><i className="fas fa-share-nodes"></i></div>
                </div>
                <div className="uv-stat-card-value">{referralStats.totals?.totalShares || 0}</div>
                <div className="uv-stat-card-description">Times your links were shared</div>
              </div>
              <div className="uv-stat-card">
                <div className="uv-stat-card-header">
                  <h3 className="uv-stat-card-title">Total Clicks</h3>
                  <div className="uv-stat-card-icon" style={{ color: "#10b981" }}><i className="fas fa-mouse-pointer"></i></div>
                </div>
                <div className="uv-stat-card-value">{referralStats.totals?.totalClicks || 0}</div>
                <div className="uv-stat-card-description">Visits from your shared links</div>
              </div>
              <div className="uv-stat-card">
                <div className="uv-stat-card-header">
                  <h3 className="uv-stat-card-title">Referred Contributors</h3>
                  <div className="uv-stat-card-icon" style={{ color: "#8b5cf6" }}><i className="fas fa-users"></i></div>
                </div>
                <div className="uv-stat-card-value">{referralStats.totals?.totalContributors || 0}</div>
                <div className="uv-stat-card-description">People who contributed via you</div>
              </div>
              <div className="uv-stat-card">
                <div className="uv-stat-card-header">
                  <h3 className="uv-stat-card-title">Amount Raised</h3>
                  <div className="uv-stat-card-icon" style={{ color: "#f59e0b" }}><i className="fas fa-coins"></i></div>
                </div>
                <div className="uv-stat-card-value">₹{formatCurrency(referralStats.totals?.totalAmount || 0)}</div>
                <div className="uv-stat-card-description">Total funds generated</div>
              </div>
            </div>

            {referralStats.projects?.length > 0 && (
              <div className="uv-contributions-table-wrapper">
                <table className="uv-contributions-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Shares</th>
                      <th>Clicks</th>
                      <th>Contributors</th>
                      <th>Raised Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralStats.projects.map((proj, idx) => (
                      <tr key={idx}>
                        <td><span className="uv-status-badge uv-status-pending">{proj.projectTitle}</span></td>
                        <td>{proj.shares}</td>
                        <td>{proj.totalClicks}</td>
                        <td>{proj.contributions}</td>
                        <td style={{ color: "#10b981", fontWeight: "bold" }}>₹{formatCurrency(proj.amountRaised)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Recent Contributions */}
        <div className="uv-recent-investments">
          <div className="uv-section-header">
            <h2 className="uv-section-title">Recent Successful Contributions</h2>
            <button
              className="uv-btn uv-btn-primary"
              onClick={() => (window.location.href = "/Discover")}
            >
              <i className="fas fa-plus"></i> Add Contribution
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>
          ) : contributions && contributions.length > 0 ? (
            <div className="uv-contributions-table-wrapper">
              <table className="uv-contributions-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Assigned Returns %</th>
                    <th>Accrued Today</th>
                    <th>Accrued Till Date</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {profitSummary?.contributions?.length > 0
                    ? profitSummary.contributions.map((c, idx) => {
                      const dayPct = (c.assignedProfitPercent / 548).toFixed(4);
                      const dayAmt = ((c.amount * parseFloat(dayPct)) / 100).toFixed(2);
                      const progressPct = Math.min(100, ((c.daysElapsed / c.totalDays) * 100)).toFixed(1);
                      return (
                        <tr key={c.id || idx}>
                          <td>
                            <div className="uv-project-info">
                              <div className="uv-project-image"><i className="fas fa-film"></i></div>
                              <div>{c.projectName || "—"}</div>
                            </div>
                          </td>
                          <td>₹{formatCurrency(c.amount)}</td>
                          <td>—</td>
                          <td><span className="uv-status-badge uv-status-success">Success</span></td>
                          <td style={{ color: "#f5a623", fontWeight: 600 }}>
                            {c.assignedProfitPercent}%
                          </td>
                          <td style={{ color: "#2ecc71" }}>
                            +{dayPct}%<br />
                            <small>₹{dayAmt}</small>
                          </td>
                          <td>
                            <span style={{ color: "#f5a623" }}>
                              {c.cumulativeProfitPercent.toFixed(3)}%
                            </span><br />
                            <small>₹{formatCurrency(c.cumulativeProfitAmount)}</small>
                          </td>
                          <td>
                            <div className="uv-cap-progress-bar">
                              <div
                                className="uv-cap-progress-fill uv-profit-fill"
                                style={{ width: `${progressPct}%` }}
                              ></div>
                            </div>
                            <small style={{ color: "#8A8580" }}>
                              {c.daysElapsed}/{c.totalDays} days
                            </small>
                          </td>
                        </tr>
                      );
                    })
                    : contributions.map((c, idx) => (
                      <tr key={c.id || idx}>
                        <td>
                          <div className="uv-project-info">
                            <div className="uv-project-image"><i className="fas fa-film"></i></div>
                            <div>
                              <div>{c.projectName || "—"}</div>
                              <div className="uv-project-type">{c.projectType || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td>₹{formatCurrency(c.amount)}</td>
                        <td>{c.date ? new Date(c.date).toLocaleString() : "—"}</td>
                        <td>
                          <span className={`uv-status-badge uv-status-${formatStatusClass(c.paymentStatus)}`}>
                            {c.paymentStatus || "N/A"}
                          </span>
                        </td>
                        <td>—</td><td>—</td><td>—</td><td>—</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="uv-no-contributions"
              style={{ padding: "2rem", textAlign: "center", color: "#e53170", fontSize: "1.1rem" }}
            >
              No successful contributions found yet. <br />
              <b>Contribute to get potential profit!</b>
            </div>
          )}
        </div>

        {showCapModal && (
          <div className="cap-modal-overlay" onClick={() => setShowCapModal(false)}>
            <div className="cap-modal" onClick={(e) => e.stopPropagation()}>

              {/* Modal Header */}
              <div className="cap-modal-header">
                <div>
                  <h2 className="cap-modal-title">CAP Rewards & Recognition Program</h2>
                  <p className="cap-modal-subtitle">Contribute or refer others to unlock exclusive rewards</p>
                </div>
                <button className="cap-modal-close" onClick={() => setShowCapModal(false)}>&#10005;</button>
              </div>

              {/* Tabs */}
              <div className="cap-modal-tabs">
                <button className={`cap-tab ${capModalTab === "milestones" ? "active" : ""}`} onClick={() => setCapModalTab("milestones")}>Milestone Rewards</button>
                <button className={`cap-tab ${capModalTab === "titles" ? "active" : ""}`} onClick={() => setCapModalTab("titles")}>Recognition Titles</button>
                <button className={`cap-tab ${capModalTab === "rules" ? "active" : ""}`} onClick={() => setCapModalTab("rules")}>Eligibility Rules</button>
              </div>

              {/* Tab Content */}
              <div className="cap-modal-body">

                {/* --- Tab 1: Milestones --- */}
                {capModalTab === "milestones" && (
                  <div className="cap-milestone-list">
                    {MILESTONES.map((m) => {
                      const isCurrent = summary.capRewards?.currentMilestone === m.milestone;
                      const isNext = summary.capRewards?.nextMilestone === m.milestone;
                      return (
                        <div key={m.milestone} className={`cap-milestone-row ${isCurrent ? "cap-row-current" : ""} ${isNext ? "cap-row-next" : ""}`}>
                          <span className="cap-tier-badge">{m.milestone}</span>
                          <div className="cap-milestone-info">
                            <div className="cap-milestone-conditions">
                              <span className="cap-cond">Rs.{Number(m.amount).toLocaleString("en-IN")}</span>
                              <span className="cap-or">OR</span>
                              <span className="cap-cond">{m.contributors} contributors</span>
                            </div>
                            <div className="cap-milestone-reward">{m.reward}</div>
                            {isCurrent && summary.capRewards?.nextMilestone && (
                              <div className="cap-milestone-progress">
                                <div className="cap-milestone-progress-fill" style={{ width: `${summary.capRewards.progressPercent}%` }}></div>
                              </div>
                            )}
                            {isCurrent && <div className="cap-current-label">Your Current Milestone</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* --- Tab 2: Recognition Titles --- */}
                {capModalTab === "titles" && (
                  <div className="cap-titles-list">
                    {MILESTONES.map((m) => {
                      const isCurrent = summary.capRewards?.currentTitle === m.title;
                      return (
                        <div key={m.title} className={`cap-title-row ${isCurrent ? "cap-row-current" : ""}`}>
                          <div className="cap-title-amount">Rs.{Number(m.amount).toLocaleString("en-IN")}</div>
                          <div className="cap-title-name">{m.title} {isCurrent && <span className="cap-your-title">Your Title</span>}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* --- Tab 3: Eligibility Rules --- */}
                {capModalTab === "rules" && (
                  <div className="cap-rules-list">
                    {[
                      "Contributor KYC Completed",
                      "Contribution Successfully Received",
                      "Referral Code Properly Mapped",
                      "No Fraudulent Activity",
                      "Compliance Guidelines Followed",
                      "Reward released after verification period",
                    ].map((rule) => (
                      <div key={rule} className="cap-rule-row">
                        <span className="cap-rule-check">&#10003;</span>
                        <span>{rule}</span>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;









